import { PAGE_PATH } from '@/lib/page-paths'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { IconType } from 'react-icons'
import { FiCalendar, FiHome, FiLink, FiMessageSquare, FiZap } from 'react-icons/fi'

type NavItem = {
  icon: IconType
  label: string
  path: string | null
}

const NAV_ITEMS: NavItem[] = [
  { icon: FiHome, label: 'Dashboard', path: PAGE_PATH.DASHBOARD },
  { icon: FiZap, label: 'Automations', path: PAGE_PATH.AUTOMATIONS },
  { icon: FiCalendar, label: 'Scheduled Tasks', path: PAGE_PATH.SCHEDULED_TASKS },
  { icon: FiMessageSquare, label: 'Chats', path: PAGE_PATH.THREADS },
  //{ icon: FiBook, label: 'Library', path: PAGE_PATH.LIBRARY },
  { icon: FiLink, label: 'Integrations', path: null },
]

type SidebarNavProps = {
  isCollapsed: boolean
  onNavClick: (item: NavItem) => void
  badgeCounts?: Partial<Record<string, number>>
}

export function SidebarNav({ isCollapsed, onNavClick, badgeCounts }: SidebarNavProps) {
  const pathname = usePathname()

  return (
    <nav className="flex flex-col gap-1 border-b border-gray-200/50 p-2">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon
        const isActive = item.path && pathname.startsWith(item.path)
        const badgeCount = badgeCounts?.[item.label] ?? 0
        const hasBadge = badgeCount > 0

        const content = (
          <>
            <div className="relative flex-shrink-0">
              <Icon className={cn('h-5 w-5', isActive ? 'text-ui-text' : 'text-ui-secondary')} />
              {hasBadge && (
                <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-500" />
              )}
            </div>
            {!isCollapsed && (
              <span className="flex flex-1 items-center justify-between whitespace-nowrap">
                {item.label}
                {hasBadge && <span className="mr-1 h-2 w-2 rounded-full bg-blue-500" />}
              </span>
            )}
          </>
        )

        const className = cn(
          'flex items-start gap-3 rounded-lg py-2 pl-3.5 text-sm font-medium text-ui-text transition-all',
          isActive ? 'bg-ui-hover' : 'hover:bg-ui-hover',
          isCollapsed && 'justify-start'
        )

        if (item.path) {
          return (
            <Link key={item.label} href={item.path}>
              <motion.div
                className={className}
                whileHover={{ x: isCollapsed ? 0 : 4 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
                title={isCollapsed ? item.label : undefined}
              >
                {content}
              </motion.div>
            </Link>
          )
        }

        return (
          <motion.button
            key={item.label}
            onClick={() => onNavClick(item)}
            className={className}
            whileHover={{ x: isCollapsed ? 0 : 4 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
            title={isCollapsed ? item.label : undefined}
          >
            {content}
          </motion.button>
        )
      })}
    </nav>
  )
}

export type { NavItem }
